import * as React from 'react';

import { classNames } from '@core/utils/functions/class-names';
import { CheckIcon } from '@heroicons/react/solid';

type TicketsPlanPrice = {
    Fiat: string;
    Crypto: string;
};

type TicketsPlanButton = {
    href: TicketsPlanPrice;
    label: string;
    target: string;
};

type TicketsPlanProps = {
    className?: string;
    title: string;
    previousPrice: TicketsPlanPrice;
    price: TicketsPlanPrice;
    description: string;
    button: TicketsPlanButton;
    features: string[];
    featured: boolean;
    activePeriod: 'Fiat' | 'Crypto';
    accentColor: string;
};

const TicketsPlan = (props: TicketsPlanProps) => {
    const {
        className,
        title,
        price,
        previousPrice,
        description,
        button,
        features,
        featured,
        activePeriod,
        accentColor,
    } = props;

    return (
        <section
            className={classNames(
                'flex flex-col overflow-hidden p-6 border min-w-[275px]',
                featured ? 'order-first lg:order-none' : 'bg-white',
                className
            )}
            style={featured ? { backgroundColor: accentColor } : {}}
        >
            <h3
                className={classNames(
                    'flex items-center text-lg uppercase font-semibold',
                    featured ? 'text-white' : 'text-gray-900'
                )}
            >
                <>{title}</>
            </h3>
            <p
                className={classNames('relative mt-5 flex text-3xl tracking-tight')}
                style={featured ? { color: 'white' } : { color: accentColor }}
            >
                {price.Fiat === price.Crypto ? (
                    price.Fiat
                ) : (
                    <>
                        <span
                            aria-hidden={activePeriod === 'Crypto'}
                            className={classNames(
                                'transition duration-300 flex flex-col',
                                activePeriod === 'Crypto' && 'pointer-events-none translate-x-6 select-none opacity-0'
                            )}
                        >
                            <span className="text-xl text-gray-300 line-through">{previousPrice.Fiat}</span>
                            <span>{price.Fiat}</span>
                        </span>
                        <span
                            aria-hidden={activePeriod === 'Fiat'}
                            className={classNames(
                                'absolute left-0 top-0 transition duration-300',
                                activePeriod === 'Fiat' && 'pointer-events-none -translate-x-6 select-none opacity-0'
                            )}
                        >
                            <span>{price.Crypto}</span>
                        </span>
                    </>
                )}
            </p>
            <p className={classNames('mt-3 text-sm', featured ? 'text-gray-100' : 'text-gray-700')}>{description}</p>
            <div className="order-last mt-6">
                <ul
                    role="list"
                    className={classNames(
                        '-my-2 divide-y text-sm',
                        featured ? 'divide-gray-300 text-gray-300' : 'divide-gray-200 text-gray-700'
                    )}
                >
                    {features.map((feature: any) => (
                        <li key={feature} className="flex py-2">
                            <CheckIcon
                                className={classNames('h-6 w-6 flex-none')}
                                style={!featured ? { color: accentColor } : { color: 'white' }}
                            />
                            <span className="ml-4">{feature}</span>
                        </li>
                    ))}
                </ul>
            </div>
            {button.label && button.href[activePeriod] && (
                <a
                    href={button.href[activePeriod]}
                    target={button.target || '_blank'}
                    style={
                        featured
                            ? { color: 'white', backgroundColor: accentColor, borderColor: 'white' }
                            : { color: accentColor, backgroundColor: 'white', borderColor: accentColor }
                    }
                    className={classNames(
                        'inline-flex justify-center py-2 px-3 text-sm font-semibold transition-colors mt-6 border hover:opacity-90'
                    )}
                    aria-label={button.label}
                >
                    {button.label}
                </a>
            )}
        </section>
    );
};

export default TicketsPlan;
